import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react'
import {
  Button, Spinner, Input, Badge,
} from 'reactstrap'
import ModeloPreview from './ModeloPreview'
import {
  getModeloItens, saveItem, setItemOrd, setItemPai, delItem, getContrato,
} from './minutaApi'
import { plainTextFromBody } from './minutaRender'
import { notifyError, notifySuccess } from '../../utils/notify'

const ROOT = '' // representação de paiId nulo

/**
 * Editor drag-and-drop de um modelo de minuta.
 * props: model, blocos, secoes, clausulas, campos, onBack
 */
const ModeloEditor = ({
  model, blocos = [], secoes = [], clausulas = [], onBack,
}) => {
  const [itens, setItens] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [overZone, setOverZone] = useState(null) // {type:'root'|'sec', secId, beforeId}
  const [contratoId, setContratoId] = useState('')
  const [contrato, setContrato] = useState(null)

  const dragRef = useRef(null) // payload do arrasto atual
  const itemRefs = useRef({}) // id do item -> elemento (para cálculo de posição no drop)

  // Dada uma lista ordenada de ids candidatos e a posição Y do cursor, devolve
  // o id do item antes do qual inserir (ou null para inserir no fim).
  const computeBeforeId = (candidateIds, clientY) => {
    for (let i = 0; i < candidateIds.length; i += 1) {
      const el = itemRefs.current[candidateIds[i]]
      if (el) {
        const r = el.getBoundingClientRect()
        if (clientY < r.top + r.height / 2) return candidateIds[i]
      }
    }
    return null
  }

  const blocoById = useMemo(() => Object.fromEntries(blocos.map((b) => [b.minutaBloco_id, b])), [blocos])
  const secById = useMemo(() => Object.fromEntries(secoes.map((s) => [s.minutaSec_id, s])), [secoes])
  const clausById = useMemo(() => Object.fromEntries(clausulas.map((c) => [c.minutaClaus_id, c])), [clausulas])

  const reload = useCallback(async () => {
    setLoading(true)
    const rows = await getModeloItens(model.minutaMod_id)
    setItens(rows || [])
    setLoading(false)
  }, [model])

  useEffect(() => { reload() }, [reload])

  // ----- derivações de hierarquia -----
  const rootItems = itens
    .filter((it) => !it.minutaModItem_paiId)
    .sort((a, b) => Number(a.minutaModItem_ord ?? 0) - Number(b.minutaModItem_ord ?? 0))

  const childrenOf = (secItemId) => itens
    .filter((it) => String(it.minutaModItem_paiId) === String(secItemId))
    .sort((a, b) => Number(a.minutaModItem_ord ?? 0) - Number(b.minutaModItem_ord ?? 0))

  const itemById = (id) => itens.find((it) => String(it.minutaModItem_id) === String(id))

  const siblingIds = (paiId, excludeId) => itens
    .filter((it) => String(it.minutaModItem_paiId || ROOT) === String(paiId || ROOT)
      && String(it.minutaModItem_id) !== String(excludeId))
    .sort((a, b) => Number(a.minutaModItem_ord ?? 0) - Number(b.minutaModItem_ord ?? 0))
    .map((it) => it.minutaModItem_id)

  // aplica ordem sequencial (10,20,...) ao grupo
  const aplicarOrdem = async (orderedIds) => {
    for (let i = 0; i < orderedIds.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await setItemOrd(orderedIds[i], (i + 1) * 10)
    }
  }

  const refKeyByTipo = { bloco: 'minutaBloco_id', secao: 'minutaSec_id', clausula: 'minutaClaus_id' }
  const refDoItem = (item) => {
    if (item.minutaModItem_tipo === 'bloco') return item.minutaBloco_id
    if (item.minutaModItem_tipo === 'secao') return item.minutaSec_id
    return item.minutaClaus_id
  }

  // ----- operações -----
  // OBS: a API do Oikos envolve todo valor em aspas (não grava NULL via insert/update).
  // Por isso o paiId só é enviado quando é um inteiro válido; "mover para a raiz"
  // é feito recriando o item (sem paiId).
  const adicionarNovo = async (tipo, refId, paiId, beforeId) => {
    setBusy(true)
    try {
      const data = {
        minutaMod_id: model.minutaMod_id,
        minutaModItem_tipo: tipo,
        minutaModItem_ord: 9999,
      }
      if (paiId) data.minutaModItem_paiId = Number(paiId)
      data[refKeyByTipo[tipo]] = Number(refId)
      const novoId = await saveItem(data)
      if (!novoId) throw new Error('sem id')

      const ids = siblingIds(paiId, novoId)
      const idx = beforeId ? ids.indexOf(beforeId) : -1
      if (idx >= 0) ids.splice(idx, 0, novoId)
      else ids.push(novoId)
      await aplicarOrdem(ids)
      await reload()
    } catch (err) {
      console.log('Erro ao adicionar item', err)
      notifyError('Erro ao adicionar item.')
    }
    setBusy(false)
  }

  const mover = async (itemId, novoPaiId, beforeId) => {
    if (String(itemId) === String(beforeId)) return
    setBusy(true)
    try {
      const item = itemById(itemId)
      if (!item) { setBusy(false); return }
      const paiAntigo = item.minutaModItem_paiId || ROOT
      const mudouPai = String(paiAntigo) !== String(novoPaiId || ROOT)

      // Mover para a raiz vindo de uma seção: recria o item (API não grava NULL).
      if (!novoPaiId && mudouPai) {
        const novoData = {
          minutaMod_id: model.minutaMod_id,
          minutaModItem_tipo: item.minutaModItem_tipo,
          minutaModItem_ord: 9999,
        }
        novoData[refKeyByTipo[item.minutaModItem_tipo]] = Number(refDoItem(item))
        const novoId = await saveItem(novoData)
        if (!novoId) throw new Error('sem id')
        await delItem(itemId)

        const ids = siblingIds(ROOT, itemId)
        const idx = beforeId ? ids.indexOf(beforeId) : -1
        if (idx >= 0) ids.splice(idx, 0, novoId)
        else ids.push(novoId)
        await aplicarOrdem(ids)
        await aplicarOrdem(siblingIds(paiAntigo, itemId))
        await reload()
        setBusy(false)
        return
      }

      if (mudouPai) await setItemPai(itemId, Number(novoPaiId))

      const ids = siblingIds(novoPaiId, itemId)
      const idx = beforeId ? ids.indexOf(beforeId) : -1
      if (idx >= 0) ids.splice(idx, 0, itemId)
      else ids.push(itemId)
      await aplicarOrdem(ids)

      if (mudouPai) await aplicarOrdem(siblingIds(paiAntigo, itemId))
      await reload()
    } catch (err) {
      console.log('Erro ao mover item', err)
      notifyError('Erro ao mover item.')
    }
    setBusy(false)
  }

  const remover = async (itemId) => {
    setBusy(true)
    try {
      const item = itemById(itemId)
      const pai = item ? (item.minutaModItem_paiId || ROOT) : ROOT
      // remove também as cláusulas-filhas se for seção
      if (item && item.minutaModItem_tipo === 'secao') {
        const filhos = childrenOf(itemId)
        // eslint-disable-next-line no-restricted-syntax
        for (const f of filhos) {
          // eslint-disable-next-line no-await-in-loop
          await delItem(f.minutaModItem_id)
        }
      }
      await delItem(itemId)
      await aplicarOrdem(siblingIds(pai, itemId))
      await reload()
    } catch (err) {
      console.log('Erro ao remover item', err)
      notifyError('Erro ao remover item.')
    }
    setBusy(false)
  }

  // ----- DnD helpers -----
  const onPaletteDragStart = (tipo, refId) => (e) => {
    dragRef.current = { kind: 'new', tipo, refId }
    e.dataTransfer.effectAllowed = 'copy'
    e.dataTransfer.setData('text/plain', `new:${tipo}:${refId}`)
  }
  const onItemDragStart = (itemId) => (e) => {
    dragRef.current = { kind: 'move', itemId }
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', `move:${itemId}`)
    e.stopPropagation()
  }
  const onDragEnd = () => { dragRef.current = null; setOverZone(null) }

  const tipoDoArrasto = () => {
    const p = dragRef.current
    if (!p) return null
    if (p.kind === 'new') return p.tipo
    const it = itemById(p.itemId)
    return it ? it.minutaModItem_tipo : null
  }

  const allowDrop = (e) => { e.preventDefault() }

  // Drop no nível raiz (antes de beforeId ou no fim)
  const handleDropRoot = (beforeId) => async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const p = dragRef.current
    setOverZone(null)
    if (!p) return
    if (p.kind === 'new') {
      // bloco e seção sempre raiz; cláusula no raiz = avulsa (permitido)
      await adicionarNovo(p.tipo, p.refId, null, beforeId)
    } else {
      await mover(p.itemId, null, beforeId)
    }
  }

  // Drop dentro de uma seção (apenas cláusulas)
  const handleDropSecao = (secItemId, beforeId) => async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const p = dragRef.current
    setOverZone(null)
    if (!p) return
    const tipo = tipoDoArrasto()
    if (tipo !== 'clausula') {
      notifyError('Apenas cláusulas podem entrar em uma seção.')
      return
    }
    if (p.kind === 'new') {
      await adicionarNovo('clausula', p.refId, secItemId, beforeId)
    } else {
      await mover(p.itemId, secItemId, beforeId)
    }
  }

  const previewContrato = async () => {
    if (!contratoId) { setContrato(null); return }
    const c = await getContrato(contratoId)
    if (!c) notifyError('Contrato não encontrado.')
    setContrato(c)
  }

  // ----- render -----
  const tituloItem = (item) => {
    if (item.minutaModItem_tipo === 'bloco') {
      const b = blocoById[item.minutaBloco_id]
      return b ? b.minutaBloco_nome : '(bloco removido)'
    }
    if (item.minutaModItem_tipo === 'secao') {
      const s = secById[item.minutaSec_id]
      return s ? s.minutaSec_nome : '(seção removida)'
    }
    const c = clausById[item.minutaClaus_id]
    return c ? c.minutaClaus_nome : '(cláusula removida)'
  }

  const renderClausulaItem = (item, dropHandlerBefore, overKey) => (
    <div
      className="minuta-item tipo-clausula"
      key={item.minutaModItem_id}
      ref={(el) => { itemRefs.current[item.minutaModItem_id] = el }}
      draggable
      onDragStart={onItemDragStart(item.minutaModItem_id)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => { allowDrop(e); e.stopPropagation(); setOverZone(overKey) }}
      onDrop={dropHandlerBefore}
      style={overZone === overKey ? { boxShadow: 'inset 0 3px 0 #564ab1' } : undefined}
    >
      <div className="minuta-item-head">
        <i className="mdi mdi-drag-vertical minuta-item-handle" />
        <span className="minuta-item-titulo">{tituloItem(item)}</span>
        <span className="minuta-item-tipo">cláusula</span>
        <Button size="sm" color="link" className="text-danger" onClick={() => remover(item.minutaModItem_id)}>
          <i className="mdi mdi-close" />
        </Button>
      </div>
    </div>
  )

  if (loading) return <div className="text-center py-5"><Spinner /></div>

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button color="light" size="sm" className="mr-2" onClick={onBack}>
            <i className="mdi mdi-arrow-left" /> Voltar
          </Button>
          <strong>{model.minutaMod_nome}</strong>
          <span className="minuta-sec-hand ml-2">{model.minutaMod_hand}</span>
          {busy && <Spinner size="sm" className="ml-2" />}
        </div>
      </div>

      <div className="minuta-builder">
        {/* Paleta */}
        <div className="minuta-palette">
          <div className="minuta-palette-group">
            <h6>Blocos</h6>
            {blocos.map((b) => (
              <div
                key={b.minutaBloco_id}
                className="minuta-chip minuta-chip-bloco"
                draggable
                onDragStart={onPaletteDragStart('bloco', b.minutaBloco_id)}
                onDragEnd={onDragEnd}
              >
                <i className="mdi mdi-shape-outline" /> {b.minutaBloco_nome}
              </div>
            ))}
            {blocos.length === 0 && <small className="text-muted">Nenhum bloco.</small>}
          </div>

          <div className="minuta-palette-group">
            <h6>Seções</h6>
            {secoes.map((s) => (
              <div
                key={s.minutaSec_id}
                className="minuta-chip minuta-chip-secao"
                draggable
                onDragStart={onPaletteDragStart('secao', s.minutaSec_id)}
                onDragEnd={onDragEnd}
              >
                <i className="mdi mdi-format-list-bulleted-type" /> {s.minutaSec_nome}
              </div>
            ))}
            {secoes.length === 0 && <small className="text-muted">Nenhuma seção.</small>}
          </div>

          <div className="minuta-palette-group">
            <h6>Cláusulas</h6>
            {clausulas.map((c) => (
              <div
                key={c.minutaClaus_id}
                className="minuta-chip minuta-chip-clausula"
                draggable
                onDragStart={onPaletteDragStart('clausula', c.minutaClaus_id)}
                onDragEnd={onDragEnd}
                title={plainTextFromBody(c.minutaClaus_body).slice(0, 120)}
              >
                <i className="mdi mdi-text-box-outline" /> {c.minutaClaus_nome}
              </div>
            ))}
            {clausulas.length === 0 && <small className="text-muted">Nenhuma cláusula.</small>}
          </div>
        </div>

        {/* Área de montagem */}
        <div
          className={`minuta-dropzone${overZone === 'root-end' ? ' is-over' : ''}`}
          onDragOver={(e) => {
            allowDrop(e)
            const beforeId = computeBeforeId(rootItems.map((it) => it.minutaModItem_id), e.clientY)
            setOverZone(beforeId ? `root-before-${beforeId}` : 'root-end')
          }}
          onDrop={(e) => {
            const beforeId = computeBeforeId(rootItems.map((it) => it.minutaModItem_id), e.clientY)
            handleDropRoot(beforeId)(e)
          }}
        >
          {rootItems.length === 0 && (
            <div className="text-muted text-center py-4">
              Arraste blocos, seções e cláusulas para cá.
            </div>
          )}

          {rootItems.map((item) => {
            if (item.minutaModItem_tipo === 'secao') {
              const filhos = childrenOf(item.minutaModItem_id)
              const zoneKey = `sec-${item.minutaModItem_id}`
              return (
                <div
                  className="minuta-item tipo-secao"
                  key={item.minutaModItem_id}
                  ref={(el) => { itemRefs.current[item.minutaModItem_id] = el }}
                  draggable
                  onDragStart={onItemDragStart(item.minutaModItem_id)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => { allowDrop(e); setOverZone(`root-before-${item.minutaModItem_id}`) }}
                  onDrop={handleDropRoot(item.minutaModItem_id)}
                  style={overZone === `root-before-${item.minutaModItem_id}` ? { boxShadow: 'inset 0 3px 0 #564ab1' } : undefined}
                >
                  <div className="minuta-item-head">
                    <i className="mdi mdi-drag-vertical minuta-item-handle" />
                    <span className="minuta-item-titulo">{tituloItem(item)}</span>
                    <span className="minuta-item-tipo">seção</span>
                    <Button size="sm" color="link" className="text-danger" onClick={() => remover(item.minutaModItem_id)}>
                      <i className="mdi mdi-close" />
                    </Button>
                  </div>
                  <div
                    className={`minuta-secao-filhos${overZone === zoneKey ? ' is-over' : ''}`}
                    onDragOver={(e) => {
                      allowDrop(e)
                      e.stopPropagation()
                      const ids = childrenOf(item.minutaModItem_id).map((f) => f.minutaModItem_id)
                      const beforeId = computeBeforeId(ids, e.clientY)
                      setOverZone(beforeId ? `secchild-${beforeId}` : zoneKey)
                    }}
                    onDrop={(e) => {
                      const ids = childrenOf(item.minutaModItem_id).map((f) => f.minutaModItem_id)
                      const beforeId = computeBeforeId(ids, e.clientY)
                      handleDropSecao(item.minutaModItem_id, beforeId)(e)
                    }}
                  >
                    {filhos.length === 0 && (
                      <div className="minuta-secao-vazia">Arraste cláusulas para esta seção</div>
                    )}
                    {filhos.map((f) => renderClausulaItem(
                      f,
                      handleDropSecao(item.minutaModItem_id, f.minutaModItem_id),
                      `secchild-${f.minutaModItem_id}`,
                    ))}
                  </div>
                </div>
              )
            }

            if (item.minutaModItem_tipo === 'bloco') {
              return (
                <div
                  className="minuta-item tipo-bloco"
                  key={item.minutaModItem_id}
                  ref={(el) => { itemRefs.current[item.minutaModItem_id] = el }}
                  draggable
                  onDragStart={onItemDragStart(item.minutaModItem_id)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => { allowDrop(e); setOverZone(`root-before-${item.minutaModItem_id}`) }}
                  onDrop={handleDropRoot(item.minutaModItem_id)}
                  style={overZone === `root-before-${item.minutaModItem_id}` ? { boxShadow: 'inset 0 3px 0 #564ab1' } : undefined}
                >
                  <div className="minuta-item-head">
                    <i className="mdi mdi-drag-vertical minuta-item-handle" />
                    <span className="minuta-item-titulo">{tituloItem(item)}</span>
                    <span className="minuta-item-tipo">bloco</span>
                    <Button size="sm" color="link" className="text-danger" onClick={() => remover(item.minutaModItem_id)}>
                      <i className="mdi mdi-close" />
                    </Button>
                  </div>
                </div>
              )
            }

            // cláusula avulsa no raiz
            return (
              <div key={item.minutaModItem_id}>
                {renderClausulaItem(
                  item,
                  handleDropRoot(item.minutaModItem_id),
                  `root-before-${item.minutaModItem_id}`,
                )}
                <span className="minuta-warning">
                  <i className="mdi mdi-alert-outline" /> cláusula avulsa (fora de seção)
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Preview */}
      <div className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Pré-visualização</h5>
          <div className="d-flex align-items-center">
            <Input
              bsSize="sm"
              placeholder="ID do contrato"
              style={{ width: 140 }}
              value={contratoId}
              onChange={(e) => setContratoId(e.target.value)}
            />
            <Button size="sm" color="light" className="ml-2" onClick={previewContrato}>
              Aplicar contrato
            </Button>
            {contrato && (
              <Badge color="success" className="ml-2">
                {contrato.contrato_num || contrato.contrato_nome || `#${contrato.contrato_id}`}
              </Badge>
            )}
          </div>
        </div>
        <ModeloPreview
          itens={itens}
          blocoById={blocoById}
          secById={secById}
          clausById={clausById}
          contrato={contrato}
        />
      </div>
    </>
  )
}

export default ModeloEditor
